import * as React from "react";
import {
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
} from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import Breadcrumbs from "../../containers/Breadcrumbs";
import { fetchData } from "../../common/services/ApiClient";
import { ApiBaseUrl } from "../../config";
import { red } from "@mui/material/colors";
import { AppBody } from "../AppBody";
import { INode, ITask } from "../../interfaces";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";

const CancelButton = withStyles(theme => ({
  root: {
    "color": theme.palette.getContrastText(red[500]),
    "backgroundColor": red[700],
    "&:hover": {
      backgroundColor: red[900],
    },
  },
}))(Button);

export default function HealthDashboard() {
  const [healthStats, setHealthStats] = React.useState([]);
  const [taskStats, setTaskStats] = React.useState([]);
  const [token, setToken] = React.useState("");

  React.useEffect(() => {
    const tokenSet: string =
      Object.fromEntries(new URLSearchParams(window.location.search).entries())
        .token || "";
    fetchData(`${ApiBaseUrl()}/health?token=${tokenSet}`)
      .then(res => {
        setHealthStats(res.data.nodes);
      })
      .catch((error: Error) => {
        console.error(error);
      });
    fetchData(`${ApiBaseUrl()}/health/tasks?token=${tokenSet}`)
      .then(res => {
        setTaskStats(res.data.tasks);
      })
      .catch((error: Error) => {
        console.error(error);
      });

    setToken(tokenSet);
  }, []);

  const headerText = (
    <Grid item xs={12}>
      <Typography variant="h1" id="dashboard">
        Health Dashboard
      </Typography>
    </Grid>
  );

  return (
    <>
      <Breadcrumbs currentPageBreadCrumbTitle="BankFind Suite: Admin Health Dashboard" />
      <CloudFoundryBanner />
      <AppBody introText={headerText}>
        <Grid container spacing={2}>
          <Grid container spacing={2} id="node-stats">
            <Grid item xs={12}>
              <Typography variant="h2" id="dashboard">
                Node Stats
              </Typography>
            </Grid>
            {healthStats.length ? (
              healthStats.map((node: INode) => {
                return (
                  <>
                    <Grid key={node.name} item xs={6}>
                      <Card>
                        <CardContent>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Typography variant="h2">
                                Node {node.name}
                              </Typography>
                              <Typography variant="h3">
                                ID {node.nodeId}
                              </Typography>
                              <Typography variant="h4">
                                Document Count :{" "}
                                {node.docCount.toLocaleString()}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="h3">JVM</Typography>
                              {Object.keys(node.javaVirtualMachine).map(
                                prop => {
                                  if (
                                    typeof node.javaVirtualMachine[
                                      prop as keyof Object
                                    ] === "object"
                                  ) {
                                    return (
                                      <Typography variant="h4" key={prop}>
                                        {splitCamelCaseToString(prop)}
                                        {Object.keys(
                                          node.javaVirtualMachine[
                                            prop as keyof Object
                                          ],
                                        ).map(prop2 => {
                                          return (
                                            <Typography key={prop2}>
                                              {splitCamelCaseToString(prop2)} :{" "}
                                              <>
                                                {
                                                  node.javaVirtualMachine[
                                                    prop as keyof Object
                                                  ][prop2 as keyof Object]
                                                }{" "}
                                              </>
                                            </Typography>
                                          );
                                        })}
                                      </Typography>
                                    );
                                  } else {
                                    return (
                                      <Typography key={prop} variant="h4">
                                        {splitCamelCaseToString(prop)} :{" "}
                                        {node.javaVirtualMachine[
                                          prop as keyof Object
                                        ].toString()}
                                      </Typography>
                                    );
                                  }
                                },
                              )}
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="h3">
                                Operating System
                              </Typography>
                              {Object.keys(node.operatingSystem).map(prop => {
                                if (
                                  typeof node.operatingSystem[
                                    prop as keyof Object
                                  ] === "object"
                                ) {
                                  return (
                                    <Typography key={prop} variant="h4">
                                      {splitCamelCaseToString(prop)}
                                      {Object.keys(
                                        node.operatingSystem[
                                          prop as keyof Object
                                        ],
                                      ).map(prop2 => {
                                        return (
                                          <Typography key={prop2}>
                                            {splitCamelCaseToString(prop2)} :{" "}
                                            <>
                                              {" "}
                                              {
                                                node.operatingSystem[
                                                  prop as keyof Object
                                                ][prop2 as keyof Object]
                                              }{" "}
                                            </>
                                          </Typography>
                                        );
                                      })}
                                    </Typography>
                                  );
                                } else {
                                  return (
                                    <Typography key={prop} variant="h4">
                                      {splitCamelCaseToString(prop)} :{" "}
                                      {node.operatingSystem[
                                        prop as keyof Object
                                      ].toString()}
                                    </Typography>
                                  );
                                }
                              })}
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  </>
                );
              })
            ) : (
              <Grid item xs={12}>
                <Typography variant="h4" color="error">
                  No Results
                </Typography>
              </Grid>
            )}
          </Grid>
          <div style={{ padding: "40px 0px 0px 0px" }} id="task-stats">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h2" id="dashboard">
                  Task Stats
                </Typography>
              </Grid>
              {taskStats.length ? (
                taskStats.map((task: ITask) => {
                  return (
                    <>
                      <Grid key={task.id} item xs={6}>
                        <Card>
                          <CardContent>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <Typography variant="h2">
                                  ID {task.id}
                                  {task.cancellable ? (
                                    <CancelButton
                                      id="cancelButton"
                                      style={{ float: "right" }}
                                      onClick={() => {
                                        cancelTask(
                                          `${task.nodeId}:${task.id}`,
                                          token,
                                        ).catch(error =>
                                          console.error(
                                            `Error: ${JSON.stringify(
                                              error,
                                              null,
                                              4,
                                            )}`,
                                          ),
                                        );
                                      }}
                                    >
                                      Cancel Task
                                    </CancelButton>
                                  ) : (
                                    <Chip
                                      style={{ float: "right" }}
                                      label="Not Cancellable"
                                      color="secondary"
                                    />
                                  )}
                                </Typography>
                                <Typography variant="h3">
                                  Node {task.nodeId}
                                </Typography>
                                <Typography variant="h4">
                                  Type : {task.action}
                                </Typography>
                                <Typography variant="h4">
                                  Running Time : {task.runningTime} ns
                                </Typography>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                    </>
                  );
                })
              ) : (
                <Grid item xs={12}>
                  <Typography variant="h4" color="error">
                    No Results
                  </Typography>
                </Grid>
              )}
            </Grid>
          </div>
        </Grid>
      </AppBody>
    </>
  );
}

function splitCamelCaseToString(prop: string) {
  return prop
    .split(/(?=[A-Z])/)
    .map((s: string) => s.charAt(0).toUpperCase() + s.substring(1))
    .join(" ");
}

async function cancelTask(taskId: string, token: string) {
  await fetch(`${ApiBaseUrl()}/health/tasks/${taskId}?token=${token}`, {
    method: "DELETE",
  })
    .then(response => {
      return response;
    })
    .catch(error => {
      console.error("Error during service worker registration:", error);
      return error;
    });
}
